import type { CSSProperties } from 'react';
import { FLAGS } from './mockData';
import type {
  Appointment,
  CellTone,
  FlagKey,
  MonthCell,
  StatusTone,
  WeekAppointment,
} from './types';
import { WEEK_DAYS } from './weekMockData';

export function cellTone(flags: FlagKey[]): CellTone {
  if (flags.some((f) => FLAGS[f]?.tone === 'red')) return 'red';
  if (flags.some((f) => FLAGS[f]?.tone === 'yellow')) return 'yellow';
  return 'green';
}

/**
 * Appointment card semantic families (Color Refinement V2 §11–§12).
 * The card's dominant signal is its STATUS family — not the provider color.
 * Five families only; each carries a light fill, a saturated rail/badge, a dark
 * label, and a matching border. Provider identity lives in the header + the
 * provider dot on large cards, not in the card fill.
 */
export type ApptFamily = 'positive' | 'warning' | 'critical' | 'active' | 'secondary';

export const APPT_FAMILY: Record<
  ApptFamily,
  { fill: string; rail: string; label: string; border: string }
> = {
  positive: { fill: '#DDF1EA', rail: '#0B7A64', label: '#0B7A64', border: '#BBDDD1' },
  warning: { fill: '#F7E8C8', rail: '#C98A12', label: '#B97810', border: '#E5CEA1' },
  critical: { fill: '#F5D9D4', rail: '#D95A4D', label: '#C94A3E', border: '#E7BFB9' },
  active: { fill: '#DCE6FA', rail: '#3E67D8', label: '#315BC7', border: '#BDD0F1' },
  secondary: { fill: '#E8DDF2', rail: '#7A58A6', label: '#6F3D9A', border: '#D5C2E4' },
};

export const STATUS_FAMILY: Record<string, ApptFamily> = {
  CONFIRMED: 'positive',
  COMPLETED: 'positive',
  CHECKED_IN: 'warning',
  ARRIVED: 'warning',
  PENDING: 'warning',
  SCHEDULED: 'warning',
  NO_SHOW: 'critical',
  BROKEN: 'critical',
  TREATMENT: 'active',
  RESCHED: 'secondary',
};

/** Cell family for a status — an unknown status renders as `secondary`. */
export function statusFamily(status: string): ApptFamily {
  return STATUS_FAMILY[status] ?? 'secondary';
}

export function appointmentFamily(status: string): {
  fill: string;
  rail: string;
  label: string;
  border: string;
} {
  return APPT_FAMILY[STATUS_FAMILY[status] ?? 'secondary'];
}

export function statusStyle(tone: StatusTone): { bg: string; fg: string } {
  switch (tone) {
    case 'success':
      return { bg: 'var(--success-tint)', fg: 'var(--success)' };
    case 'success-solid':
      return { bg: 'color-mix(in srgb, var(--success) 20%, transparent)', fg: 'var(--success)' };
    case 'gold':
      return { bg: 'var(--gold-tint)', fg: 'var(--gold)' };
    case 'ai':
      return { bg: 'var(--ai-tint)', fg: 'var(--ai-3)' };
    case 'info':
      return { bg: 'var(--info-tint)', fg: 'var(--info)' };
    case 'secondary':
      return { bg: 'var(--ai-tint)', fg: 'var(--ai-3)' };
    case 'warning':
      return { bg: 'var(--gold-tint)', fg: 'var(--gold)' };
    case 'danger':
      return { bg: 'var(--terracotta-tint)', fg: 'var(--terracotta)' };
    default:
      return { bg: 'var(--cream-0)', fg: 'var(--ink-2)' };
  }
}

export const TONE_STYLES: Record<CellTone | 'ctx', { fg: string; tint: string }> = {
  green: { fg: 'var(--success)', tint: 'var(--success-tint)' },
  yellow: { fg: 'var(--gold)', tint: 'var(--gold-tint)' },
  red: { fg: 'var(--terracotta)', tint: 'var(--terracotta-tint)' },
  ctx: { fg: 'var(--ai-3)', tint: 'var(--ai-tint)' },
};

export function splitLead(text: string): [string, string | null] {
  const i = text.indexOf(' — ');
  return i < 0 ? [text, null] : [text.slice(0, i), text.slice(i + 3)];
}

export function insightColor(tone: 'gold' | 'ai' | 'red'): string {
  return tone === 'gold' ? 'var(--gold)' : tone === 'ai' ? 'var(--ai-3)' : 'var(--terracotta)';
}

export function calendarEyebrow(extra?: CSSProperties): CSSProperties {
  return {
    fontFamily: 'var(--font-mono)',
    fontSize: 9.5,
    fontWeight: 600,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: 'var(--ink-3)',
    ...extra,
  };
}

export function formatCalendarPageDate(date = new Date()) {
  const dayTitle = date.toLocaleDateString('en-US', { weekday: 'long' });
  const daySubtitle = `, ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  return { dayTitle, daySubtitle };
}

export function formatWeekTitleRange(startDate: string, endDate: string) {
  const start = new Date(`${startDate}T12:00:00`);
  const end = new Date(`${endDate}T12:00:00`);
  const startMonth = start.toLocaleDateString('en-US', { month: 'long' });
  const endMonth = end.toLocaleDateString('en-US', { month: 'long' });
  return {
    startMonth,
    startDay: start.getDate(),
    endDay: end.getDate(),
    ...(startMonth !== endMonth ? { endMonth } : {}),
  };
}

/**
 * Quick-filter predicate — the day/week grid's one-tap spotlights.
 * Shared by day and week (both carry `status` + `flags`).
 */
export function matchesQuickFilter(
  a: Pick<Appointment, 'status' | 'flags'>,
  quick: string,
): boolean {
  switch (quick) {
    case 'all':
      return true;
    case 'red':
      return cellTone(a.flags) === 'red';
    case 'unconfirmed':
      return a.flags.includes('unconfirmed');
    case 'notready':
      return a.flags.some(
        (f) => f === 'forms_inc' || f === 'elig_fail' || f === 'lab_missing' || f === 'preauth_p',
      );
    case 'building':
      return a.status === 'ARRIVED' || a.status === 'CHECKED_IN' || a.status === 'TREATMENT';
    case 'newpt':
      return a.flags.includes('new_pt');
    default:
      return cellTone(a.flags) !== 'green';
  }
}

export function filterAppointments(appts: Appointment[], quick: string): Appointment[] {
  if (quick === 'all') return appts;
  return appts.filter((a) => matchesQuickFilter(a, quick));
}

export function buildMonthCells(referenceDate = new Date()): (MonthCell | null)[] {
  const firstDow = 2;
  const total = 31;
  const todayDay = referenceDate.getDate();
  const cells: (MonthCell | null)[] = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= total; d++) {
    const dow = (firstDow + d - 1) % 7;
    const weekend = dow >= 5;
    const s = (d * 37) % 13;
    const appts = weekend ? (d % 3 === 0 ? 4 : 0) : 8 + s;
    const attention = appts ? s % 4 : 0;
    const issue = appts && s % 6 === 0 ? 1 : 0;
    const done = Math.max(0, appts - attention - issue);
    const prod = appts * (180 + s * 22);
    cells.push({
      d,
      dow,
      weekend,
      appts,
      done,
      attention,
      issue,
      prod,
      today: d === todayDay,
      past: d < todayDay,
    });
  }
  while (cells.length % 7) cells.push(null);
  return cells;
}

export function monthMaxAppts(cells: (MonthCell | null)[]): number {
  return Math.max(...cells.filter((c): c is MonthCell => c !== null).map((c) => c.appts), 1);
}

export const MONTH_WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

export function formatWeekPageTitle(days = WEEK_DAYS) {
  const first = days[0];
  const last = days[days.length - 1];
  if (!first || !last) return { startTitle: '', endSubtitle: '' };
  const startTitle = first.full.replace(/^\w+\s/, '');
  const endNum = last.num;
  return { startTitle, endSubtitle: ` – ${endNum}` };
}

export function filterWeekAppointments(appts: WeekAppointment[], quick: string): WeekAppointment[] {
  if (quick === 'all') return appts;
  return appts.filter((a) => matchesQuickFilter(a, quick));
}
